import {
  type AllProductDTO,
  type MergedCoupon,
  type MergedCouponMap,
  type VariationsGroupByFirstLevelButton,
} from '@cart-services/types'
import { CouponType } from '@hooks-api/types/dataModel/coupon'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseStmCouponFilterParams {
  tabCouponIds: number[]
  mergedCouponMap: MergedCouponMap
  tabProducts: AllProductDTO[] | undefined
}

interface UseStmCouponFilterReturn {
  tabCoupons: MergedCoupon[]
  selectedCouponIds: Set<number>
  toggleCoupon: (couponId: number) => void
  selectAllTabCoupons: () => void
  deselectAllCoupons: () => void
  setSelectedCouponIds: (ids: Set<number>) => void
  filteredProducts: AllProductDTO[]
}

const regroupByFirstLevelButton = (variations: Variation[]): VariationsGroupByFirstLevelButton =>
  variations.reduce<VariationsGroupByFirstLevelButton>((acc, variation) => {
    const firstLevelButton = variation.first_level_button
    if (!firstLevelButton) {
      acc['其他'] = [...(acc['其他'] || []), variation]
    }
    else {
      acc[firstLevelButton] = [...(acc[firstLevelButton] || []), variation]
    }
    return acc
  }, {})

export const useStmCouponFilter = ({
  tabCouponIds,
  mergedCouponMap,
  tabProducts,
}: UseStmCouponFilterParams): UseStmCouponFilterReturn => {
  const [selectedCouponIds, setSelectedCouponIds] = useState<Set<number>>(new Set())

  const tabCouponIdsKey = tabCouponIds.join(',')

  useEffect(function resetSelectedCouponsOnTabChange() {
    setSelectedCouponIds(new Set())
  }, [tabCouponIdsKey])

  const allProductCoupons = useMemo(() => {
    return Array.from(mergedCouponMap.values()).filter((coupon) => {
      return coupon.couponTypes.has(CouponType.ALL_PRODUCT)
    })
  }, [mergedCouponMap])

  const tabCoupons = useMemo(() => {
    const tabSpecificCoupons = tabCouponIds
      .map(couponId => mergedCouponMap.get(couponId))
      .filter((coupon): coupon is MergedCoupon => Boolean(coupon))

    return [...allProductCoupons, ...tabSpecificCoupons]
  }, [tabCouponIds, mergedCouponMap, allProductCoupons])

  const hasAllProductCouponSelected = useMemo(() => {
    if (selectedCouponIds.size === 0) return false

    for (const couponId of selectedCouponIds) {
      const coupon = mergedCouponMap.get(couponId)
      if (coupon?.couponTypes.has(CouponType.ALL_PRODUCT)) return true
    }

    return false
  }, [selectedCouponIds, mergedCouponMap])

  const toggleCoupon = useCallback((couponId: number) => {
    setSelectedCouponIds((prev) => {
      const next = new Set(prev)
      if (next.has(couponId)) {
        next.delete(couponId)
      }
      else {
        next.add(couponId)
      }
      return next
    })
  }, [])

  const selectAllTabCoupons = useCallback(() => {
    const allProductCouponIds = allProductCoupons.map(coupon => coupon.coupon_id)
    setSelectedCouponIds(new Set([...allProductCouponIds, ...tabCouponIds]))
  }, [allProductCoupons, tabCouponIds])

  const deselectAllCoupons = useCallback(() => {
    setSelectedCouponIds(new Set())
  }, [])

  const filteredProducts = useMemo(() => {
    if (!tabProducts) return []
    if (selectedCouponIds.size === 0) return tabProducts
    if (hasAllProductCouponSelected) return tabProducts

    return tabProducts
      .map((product) => {
        const filteredVariations = product.variations.filter((variation) => {
          return variation.coupon_ids?.some(couponId => selectedCouponIds.has(couponId))
        })

        return {
          ...product,
          variations: filteredVariations,
          variationsGroupByFirstLevelButton: regroupByFirstLevelButton(filteredVariations),
        }
      })
      .filter(product => product.variations.length > 0)
  }, [tabProducts, selectedCouponIds, hasAllProductCouponSelected])

  return {
    tabCoupons,
    selectedCouponIds,
    toggleCoupon,
    selectAllTabCoupons,
    deselectAllCoupons,
    setSelectedCouponIds,
    filteredProducts,
  }
}
